import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { FileText, Video, Zap, Image, LogOut, Home } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    articles: 0,
    videos: 0,
    shorts: 0,
    covers: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    const fetchStats = async () => {
      try {
        setLoading(true);
        const [articlesRes, videosRes, shortsRes, coversRes] = await Promise.all([
          axios.get(`${API_URL}/api/articles?pageSize=1`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_URL}/api/videos?pageSize=1`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_URL}/api/shorts?pageSize=1`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_URL}/api/covers?pageSize=1`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
        ]);

        setStats({
          articles: articlesRes.data.length,
          videos: videosRes.data.length,
          shorts: shortsRes.data.length,
          covers: coversRes.data.length,
        });
      } catch (error) {
        if (error.response?.status === 401) {
          toast.error('Session expired. Please login again.');
          localStorage.removeItem('admin_token');
          navigate('/admin/login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const quickLinks = [
    { title: 'Articles', icon: FileText, href: '/admin/articles', count: stats.articles, color: 'text-blue-400' },
    { title: 'Videos', icon: Video, href: '/admin/videos', count: stats.videos, color: 'text-purple-400' },
    { title: 'Shorts', icon: Zap, href: '/admin/shorts', count: stats.shorts, color: 'text-yellow-400' },
    { title: 'Covers', icon: Image, href: '/admin/covers', count: stats.covers, color: 'text-green-400' },
    { title: 'Newsletter', icon: () => <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>, href: '/admin/newsletter', count: 0, color: 'text-pink-400' },
    { title: 'Settings', icon: () => <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>, href: '/admin/settings', count: '', color: 'text-orange-400' },
  ];

  return (
    <>
      <Helmet>
        <title>Admin Dashboard · B&B Magazine</title>
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur">
          <div className="container max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex h-16 items-center justify-between">
              <h1 className="font-display text-2xl font-bold">
                <span className="text-primary">B&B</span> Admin
              </h1>
              <div className="flex items-center gap-2">
                <Button asChild variant="ghost" size="sm">
                  <Link to="/">
                    <Home className="mr-2 h-4 w-4" />
                    View Site
                  </Link>
                </Button>
                <Button onClick={handleLogout} variant="ghost" size="sm">
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="container max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="mb-8">
            <h2 className="font-display text-3xl font-bold mb-2">Dashboard</h2>
            <p className="text-muted-foreground">Manage your content from here</p>
          </div>

          {/* Quick Links Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {quickLinks.map((link) => (
              <Link key={link.href} to={link.href}>
                <Card className="bg-[hsl(var(--surface-2))] border-border hover:border-primary/30 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_20px_60px_rgba(0,0,0,0.45)] group">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <link.icon className={`h-8 w-8 ${link.color} group-hover:scale-110 transition-transform duration-200`} />
                      {!loading && (
                        <span className="text-3xl font-display font-bold">{link.count}+</span>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardTitle className="font-display text-xl">{link.title}</CardTitle>
                    <CardDescription>Manage {link.title.toLowerCase()}</CardDescription>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          {/* Recent Activity */}
          <Card className="bg-[hsl(var(--surface-2))] border-border">
            <CardHeader>
              <CardTitle className="font-display">Welcome Back!</CardTitle>
              <CardDescription>
                Use the cards above to navigate to different content sections.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>• Create, edit, and delete articles, videos, shorts, and covers</p>
                <p>• Manage published and draft content</p>
                <p>• All changes are saved automatically</p>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    </>
  );
}